/**
 * Turning an API failure into something a traffic manager can act on.
 *
 * Switching happens on `problem.code` — a stable ASCII identifier — and never on
 * `title`, which the server localises from `Accept-Language` and will change
 * under us. Where we have no better wording for a code, the server's own
 * localised `detail` is used: it is already in the user's language and is more
 * specific than anything generic we could substitute.
 */

import {
  DecaApiError,
  isValidationProblem,
  type FieldError,
  type PassengerFieldError,
  type PassengerValidationProblem,
  type ValidationProblem,
} from '@deca/api'
import type { TFunction } from 'i18next'

import type { AddressBookKind } from './addressBook.ts'

/**
 * What `fetch` rejects with when a request gets no answer at all: offline, the server unreachable, the
 * connection cut. The text is the browser's own, in English whatever the portal's language, and each engine
 * has its own; Chromium's was read off a refused connection and off one cut mid-answer (Chrome 152).
 */
const FAILED_FETCH_TEXTS = new Set([
  'Failed to fetch', // Chrome, Edge
  'NetworkError when attempting to fetch resource.', // Firefox
  'Load failed', // Safari
])

/**
 * A request that got no answer, which `errors.network` says in the user's language.
 *
 * Known by its text and not by its type alone: a bug throws a `TypeError` too, and telling the user the server
 * could not be reached would send them to try again, when a create may already have gone through.
 */
export function isFailedFetch(error: unknown): boolean {
  return error instanceof TypeError && FAILED_FETCH_TEXTS.has(error.message)
}

export function apiErrorMessage(error: unknown, t: TFunction): string {
  if (isFailedFetch(error)) return t('errors.network')
  if (!(error instanceof DecaApiError)) {
    return error instanceof Error ? error.message : t('errors.generic')
  }

  const specific = t(`errors.code.${error.code}`, { defaultValue: '' })
  if (specific) return specific

  // A bare `NOT_FOUND` is the API's "no such route"; a missing *thing* always carries a
  // typed code (`DECA_NOT_FOUND`, `USER_NOT_FOUND`, …). Without this the two are the same
  // message, and an endpoint the backend has not shipped yet reads as "no se ha encontrado
  // el documento" while the document is on screen in front of the user.
  if (error.isNotFound && error.code === 'NOT_FOUND') return t('errors.notImplemented')

  if (error.status === 0) return t('errors.network')
  if (error.isUnauthorized) return t('errors.unauthorized')
  if (error.isForbidden) return t('errors.forbidden')
  if (error.isNotFound) return t('errors.notFound')
  if (error.isVersionConflict) return t('errors.conflict')
  if (error.isRateLimited) {
    return t('errors.rateLimited', { seconds: error.retryAfterSeconds ?? 60 })
  }

  return error.problem.detail || error.problem.title || t('errors.generic')
}

/** The 422 with `partyErrors`, when that is what came back — otherwise null. */
export function validationProblemOf(error: unknown): ValidationProblem | null {
  if (!(error instanceof DecaApiError)) return null
  return isValidationProblem(error.problem) ? error.problem : null
}

export function isConflict(error: unknown): boolean {
  return error instanceof DecaApiError && error.isVersionConflict
}

/**
 * #92 — the `409` for an `externalRef` another document of the company already carries. A `409`
 * like the draft that is no longer one, but about a field, so the forms mark that field; and never
 * `isConflict`, whose advice to reload would not help.
 */
export function isExternalRefTaken(error: unknown): boolean {
  return error instanceof DecaApiError && error.code === 'EXTERNAL_REF_TAKEN'
}

/**
 * #159 — the entry to update instead, when «Guardar en la libreta» is refused because the caller's
 * own book already holds that NIF (`409 ADDRESS_BOOK_ENTRY_EXISTS`); null for any other failure.
 *
 * The server names the entry in `entryId`, spread flat onto the problem body like `conflicts`
 * below. A refusal that names none is answered as an ordinary failure: offering to update an entry
 * nobody identified would update nothing.
 */
export function existingAddressBookEntryOf(error: unknown): string | null {
  if (!(error instanceof DecaApiError) || error.code !== 'ADDRESS_BOOK_ENTRY_EXISTS') return null
  const entryId = error.problem.entryId
  return typeof entryId === 'string' && entryId !== '' ? entryId : null
}

/**
 * #221 — a refused entry said in its kind's words. The catalogue's `ADDRESS_BOOK_ENTRY_EXISTS` and
 * `ADDRESS_BOOK_ENTRY_INVALID` speak of a party's NIF/CIF, which a place, a plate or a driver has
 * not. A party's refusal, and any other failure, as `apiErrorMessage` says it.
 */
export function addressBookErrorMessage(error: unknown, kind: AddressBookKind, t: TFunction): string {
  if (kind !== 'PARTY' && error instanceof DecaApiError) {
    if (error.code === 'ADDRESS_BOOK_ENTRY_EXISTS') return t(`addressBook.kinds.${kind}.exists`)
    if (error.code === 'ADDRESS_BOOK_ENTRY_INVALID') return t(`addressBook.kinds.${kind}.invalid`)
  }
  return apiErrorMessage(error, t)
}

/**
 * The `422` for an address whose domain cannot receive mail (`EMAIL_DOMAIN_UNDELIVERABLE`), as the
 * sentence for the address field, naming the domain; null for any other failure.
 *
 * On the field rather than in the dialog's notice, because the address is what has to change. The
 * case behind it is an invitation to `@gmail.co` (2026-09-24), a domain that takes no mail. The
 * server names the domain in `domain`; without it the sentence is the catalogue's own, which does
 * not name one.
 */
export function undeliverableEmailMessage(error: unknown, t: TFunction): string | null {
  if (!(error instanceof DecaApiError) || error.code !== 'EMAIL_DOMAIN_UNDELIVERABLE') return null
  const domain = error.problem.domain
  return typeof domain === 'string' && domain !== ''
    ? t('errors.emailDomainUndeliverable', { domain })
    : t('errors.code.EMAIL_DOMAIN_UNDELIVERABLE')
}

/**
 * The same `422`, answering a resend in Usuarios (#141): the address was stored long ago, and the
 * sentence for a field — "check that it is spelt correctly" — asks a company administrator for
 * something only the platform may do. So it says that nothing was sent, and what to do next in the
 * words of whoever pressed it: «Cambiar el correo» for a platform administrator, ask the operator
 * for anyone else. Null for any other failure, and for a refusal that names no domain.
 */
export function undeliverableResendMessage(error: unknown, t: TFunction, canChangeEmail: boolean): string | null {
  if (!(error instanceof DecaApiError) || error.code !== 'EMAIL_DOMAIN_UNDELIVERABLE') return null
  const domain = error.problem.domain
  if (typeof domain !== 'string' || domain === '') return null
  const next = canChangeEmail
    ? t('admin.users.address.fix', { action: t('admin.users.changeEmail') })
    : t('admin.users.address.ask', { operator: t('app.operator') })
  return `${t('admin.users.address.resendRefused', { domain })} ${next}`
}

/**
 * The route schema's refusal of an address, as the sentence for the address field; null for any
 * other failure.
 *
 * The portal checks the format before sending (`deca/emailAddress.ts`), so this is for what is left:
 * the length, which the portal does not count, and anything the two rules may come to disagree on.
 * Without it the dialog's notice shows Fastify's own English, `body/email must match format
 * "email"`, under a Spanish screen — which is how `prueba@gmail.coprueba@gmail.co` read on
 * 2026-09-25.
 *
 * **The one decision in this file taken on `detail` rather than `code`.** The schema answers before
 * any handler, so the refusal has no code of its own: it is the generic `BAD_REQUEST` of
 * `backend/src/http/app.ts`'s error handler, whose `detail` is Ajv's message, led by the path of the
 * property it refuses. `property` is that path, as the calling dialog shaped its body: `body/email`,
 * or `body/admin/email` for a new company's administrator.
 */
export function invalidEmailMessage(error: unknown, t: TFunction, property: string): string | null {
  if (!(error instanceof DecaApiError) || error.code !== 'BAD_REQUEST') return null
  const detail = error.problem.detail
  return typeof detail === 'string' && detail.startsWith(`${property} `) ? t('errors.invalidEmail') : null
}

/**
 * #180 — the `422` for an automatic finalisation period the company's download window does not allow
 * (`AUTOCOMPLETE_PERIOD_EXCEEDS_WINDOW`), as the sentence naming that window and the longest period it
 * allows; null for any other failure.
 *
 * The catalogue's sentence alone says only that the period is too long. A company administrator, who
 * cannot see the window, was left to try 7, 5 and 4 days in turn, and the platform shortening the
 * window in *Editar empresa* was told the period was too long. The server names both numbers,
 * `directDownloadDays` and `maxAutocompleteHours` (always one of the periods offered); without them, or
 * with a period the catalogue cannot name, the sentence is the catalogue's own.
 */
export function autocompletePeriodRefusal(error: unknown, t: TFunction): string | null {
  if (!(error instanceof DecaApiError) || error.code !== 'AUTOCOMPLETE_PERIOD_EXCEEDS_WINDOW') return null
  const { directDownloadDays: days, maxAutocompleteHours: hours } = error.problem
  const longest = typeof hours === 'number' ? t(`admin.company.autocompleteOptions.${hours}`, { defaultValue: '' }) : ''
  return typeof days === 'number' && longest
    ? t('errors.autocompletePeriodExceedsWindow', { days, longest })
    : t('errors.code.AUTOCOMPLETE_PERIOD_EXCEEDS_WINDOW')
}

/**
 * The route sheet's 422, when that is what came back — otherwise null.
 *
 * ⚠️ **`validationProblemOf` above cannot answer this**, and silently: the client's
 * `isValidationProblem` tests `Array.isArray(problem.partyErrors)`, and a passenger
 * problem carries `fieldErrors` instead — flat, with no parties and no penalty notice,
 * because FOM/1230/2013 has no liable-party split (design D4, client answer 1 of
 * 2026-09-09). So a passenger 422 read through the goods helper returns `null`, the
 * field errors never reach the fields, and the user is shown a generic message while the
 * server has said exactly what is wrong. Hence a second, passenger-shaped test.
 *
 * Hand-written for the reason `conflictsOf` below is: the contract declares the shape but
 * the generated client ships no predicate for it.
 */
export function passengerValidationProblemOf(error: unknown): PassengerValidationProblem | null {
  if (!(error instanceof DecaApiError)) return null
  const problem = error.problem as PassengerValidationProblem
  return Array.isArray(problem.fieldErrors) ? problem : null
}

/**
 * A passenger 422's errors, keyed the way the form addresses its fields.
 *
 * The passenger twin of the client's `errorsByField`, and the two differ in one place
 * that matters: the line-item pointer is `servicioSeq`, not `consignmentSeq`, and it
 * points at a service. The contract names it that deliberately — "a route sheet has no
 * consignments, so `consignmentSeq` would have read as a copy-paste defect for as long as
 * this contract lives".
 *
 * A `PassengerFieldError` satisfies the goods `FieldError` structurally (same five
 * required members; only the line-item pointer's name differs), so the result drops
 * straight into `DecaFormState.serverErrors` and renders through `BaseField` unchanged.
 */
export function passengerErrorsByField(
  problem: PassengerValidationProblem,
): Record<string, FieldError> {
  const out: Record<string, FieldError> = {}
  for (const error of (problem.fieldErrors ?? []) as PassengerFieldError[]) {
    const key = error.servicioSeq == null ? error.field : `services.${error.servicioSeq}.${error.field}`
    out[key] = error
  }
  return out
}

/**
 * A **thing** that is not there, as against a failure to fetch it.
 *
 * Screens read differently on the two: a missing document is not an incident, so it wants the
 * empty state and a way back, not an alert and a *Reintentar* that can only fail again.
 *
 * The `code !== 'NOT_FOUND'` half carries the distinction `apiErrorMessage` above already makes and
 * that this repo fixed once: a **typed** `DECA_NOT_FOUND` / `USER_NOT_FOUND` means the resource is
 * missing, while a bare `NOT_FOUND` is the API reporting no such route — an endpoint the backend
 * has not shipped. Collapsing them tells a user their document is gone while it is on screen.
 */
export function isResourceNotFound(error: unknown): boolean {
  return error instanceof DecaApiError && error.isNotFound && error.code !== 'NOT_FOUND'
}

/**
 * One element of grouping's 422 `conflicts` array — which document, and why it cannot join
 * the group. Mirrors `backend/src/deca/grouping.ts`'s `GroupConflict` by hand: the contract
 * declares this field `additionalProperties: true` with no fixed schema, so there is no
 * generated type to import instead. `documentNumber` is `string`, not nullable, because the
 * backend only ever reports on sources that were resolved by id and therefore exist.
 */
export interface GroupConflict {
  documentId: string
  documentNumber: string
  reason: 'PARTIES_DIFFER' | 'VEHICLE_OR_DRIVER_DIFFERS' | 'NOT_GROUPABLE_STATE' | 'ALREADY_GROUPED' | 'SOURCE_CHANGED'
  detail: string
}

/**
 * The 422's per-document reasons, when that is what came back — otherwise null.
 *
 * `conflicts` arrives spread flat onto the problem body, not nested under an `extensions`
 * key: `ProblemError` spreads its `extensions` members onto the response
 * (`backend/src/http/problem.ts:29-45`), so `error.problem.conflicts` is where it lands.
 *
 * **This is also the rule for which grouping code needs a catalogue entry**, because it decides
 * which of the two renderers runs (`GroupNotices`): a problem carrying `conflicts` is rendered
 * per document from the server's own `detail`, and its `errors.code.*` entry is never read; a
 * problem carrying none falls through to `apiErrorMessage`, where a missing entry means the
 * server's Spanish `detail` is shown to an English user.
 *
 * Conflict-free by construction, so they need entries and have them:
 * `GROUPING_COUNT_INVALID` (`backend/src/http/routes/decas.ts`, the 2..50 and de-duplication
 * rules — reachable from the portal, since a hand-edited or pasted `?ids=a&ids=a` passes the
 * screen's own "at least two" check and collapses to one on the server) and
 * `GROUPING_GENERATE_REQUIRED` (which today's portal cannot provoke: it always sends
 * `generate: true`).
 *
 * `GROUPING_PARTIES_DIFFER`, `GROUPING_VEHICLE_DIFFERS` and `GROUPING_NOT_GROUPABLE` are the
 * opposite case: the route builds them from `headlineFor`, which it calls only when
 * `conflicts.length > 0`, so **today they always arrive with a populated array and their
 * catalogue entries are never rendered**. They are kept deliberately, and not as coverage: they
 * are the fallback if a future refusal reuses one of those codes without a per-document array,
 * and `group-screen.test.tsx` exercises exactly that path. Read them as insurance, not as
 * evidence that those three refusals are localised — the text a user actually sees for them is
 * `group.conflict.*` plus the server's `detail`.
 *
 * `GROUPING_SOURCE_CHANGED` (#110's review) arrives with `conflicts` too, and is the exception:
 * `GroupNotices` leads its list with the catalogue entry instead of "cannot be grouped", because
 * nothing is wrong with a source that merely moved — the operator is to look again and resend.
 */
export function conflictsOf(error: unknown): GroupConflict[] | null {
  if (!(error instanceof DecaApiError)) return null
  const raw = error.problem.conflicts
  return Array.isArray(raw) && raw.length > 0 ? (raw as unknown as GroupConflict[]) : null
}
